// Helpers for parsing string-format tool arguments from OpenAI tool calls

import type { FinishFileSpec } from './types.js';

/** Parse "lines" string from read tool (e.g. "1-50" or "1-20,45-80") into structured args */
export function parseReadLines(linesStr: string): { start?: number; end?: number; lines?: Array<[number, number]> } {
  const ranges: Array<[number, number]> = [];
  for (const rangeStr of linesStr.split(',')) {
    const trimmed = rangeStr.trim();
    if (!trimmed) continue;
    const parts = trimmed.split('-').map(v => parseInt(v.trim(), 10));
    if (parts.length >= 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
      ranges.push([parts[0], parts[1]]);
    } else if (Number.isFinite(parts[0])) {
      ranges.push([parts[0], parts[0]]);
    }
  }
  if (ranges.length === 1) return { start: ranges[0][0], end: ranges[0][1] };
  if (ranges.length > 1) return { lines: ranges };
  return {};
}

/** Parse "files" string from finish tool (e.g. "src/auth.py:1-50\nsrc/user.py" or space-separated) into FinishFileSpec[] */
export function parseFinishFiles(filesStr: string): FinishFileSpec[] {
  const files: FinishFileSpec[] = [];
  // Split on whitespace (training uses space-separated, SDK also supports newlines)
  for (const line of filesStr.trim().split(/\s+/)) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    // Skip Windows drive letter prefix (e.g. "C:") to avoid splitting on it
    const searchFrom = /^[A-Za-z]:/.test(trimmed) ? 2 : 0;
    const colonIdx = trimmed.indexOf(':', searchFrom);
    if (colonIdx === -1) {
      files.push({ path: trimmed, lines: '*' });
      continue;
    }
    const filePath = trimmed.slice(0, colonIdx);
    const rangesPart = trimmed.slice(colonIdx + 1);
    if (!rangesPart.trim() || rangesPart.trim() === '*') {
      files.push({ path: filePath, lines: '*' });
      continue;
    }
    const ranges: Array<[number, number]> = [];
    for (const rangeStr of rangesPart.split(',')) {
      const rt = rangeStr.trim();
      if (!rt) continue;
      const parts = rt.split('-').map(v => parseInt(v.trim(), 10));
      if (parts.length >= 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
        ranges.push([parts[0], parts[1]]);
      } else if (Number.isFinite(parts[0])) {
        ranges.push([parts[0], parts[0]]);
      }
    }
    files.push({ path: filePath, lines: ranges.length > 0 ? ranges : '*' });
  }
  return files;
}

/** Extract directory path from a list_directory command string (e.g. "ls -la src/" → "src/") */
export function extractPathFromCommand(command: string): string {
  const tokens = command.trim().split(/\s+/);
  const pathTokens = tokens.slice(1).filter(t => !t.startsWith('-') && !t.startsWith('|') && !t.startsWith('\\('));
  return pathTokens[0] || '.';
}
